import { toFixed } from '@appsinti/common-utils';
import { Language } from '@appsinti/i18n/interfaces/Language';
import type {ChartOptions, TooltipItem} from 'chart.js';
import { enUS, es } from 'date-fns/locale';
import {format, parse} from 'date-fns'
import COLORS from '../../../../resources/constants/colors';
import { BG } from '@etiquette-ui/colors';
import { formatInTimeZone } from 'date-fns-tz'
import { formatMoneyMK } from "@appsinti/common-utils/textFormat";

const Locales: { [key in Language]: Locale } = {
  [Language.English]: enUS,
  [Language.Spanish]: es,
}

type Axis = {
  min?: number | string,
  max?: number | string,
  hasTextAsValue?: boolean,
  reverse ?: boolean
};

type Annotation = {
  key: string;
  title: string;
  label: string;
  xAxisLimits?: {
    min?: number | string;
    max?: number | string;
  };
  yAxisLimits?: {
    min?: number | string;
    max?: number | string;
  };
};

export type LineOptions = {
  isStacked?: boolean,
  isPercentageSet?: boolean,
  isCurrency?: boolean,
  isTimeBased?: boolean,
  annotations?: Annotation[],
  xAxis?: Axis,
  yAxis?: Axis,
  timeOptions?: {
    unit?: false | "millisecond" | "second" | "minute" | "hour" | "day" | "week" | "month" | "quarter" | "year",
    displayFormats?: {
      millisecond?: string,
      second?: string,
      minute?: string,
      hour?: string,
      day?: string,
      week?: string,
      month?: string,
      quarter?: string,
      year?: string,
    },
  },
  language?: Language,
}

type CustomLabels = {
  [key: string]: string;
};
export const getOptions: (quotes: LineOptions, customLabels: CustomLabels, annotationVisibility?: { [key: string]: boolean}) => ChartOptions<"line"> = ({
  isStacked = false,
  isPercentageSet = false,
  isCurrency = true,
  annotations = [],
  xAxis = { hasTextAsValue: false },
  yAxis = { hasTextAsValue: false },
  isTimeBased = false,
  timeOptions,
  language = Language.English
}, customLabels, annotationVisibility) => {
  let xTicksConfiguration: any;
  if (isTimeBased || xAxis.hasTextAsValue) {
    if (customLabels) {
      xTicksConfiguration = {
        callback: (val) => {
          const utcDate = formatInTimeZone(new Date(val), 'UTC', "yyyy-MM-dd'T'HH:mm:ssXXX");
          if (customLabels[utcDate] !== undefined) return customLabels[utcDate];
        },
        source: 'labels',
      };

    } else {
      xTicksConfiguration = {};
    }
  } else {
    xTicksConfiguration = {
      callback: (val) => {
        if (typeof val === 'string') return val;
        return formatNumber(val, isPercentageSet, isCurrency);
      }
    };
  }

  const options: ChartOptions<"line"> = {
    responsive: true,
    indexAxis: 'x',
    scales: {
      x: {
        type: isTimeBased ? 'time' : 'category',
        time: {
          ...timeOptions,
        },
        grid: {
          display: false,
        },
        stacked: isStacked ? true : false,
        ticks: xTicksConfiguration,
        bounds: 'data',
        min: xAxis.min,
        max: xAxis.max,
        adapters: {
          date: {
            locale: Locales[language],
          }
        },
        reverse : xAxis.reverse || false
      },
      y: {
        stacked: isStacked ? true : false,
        ticks: yAxis.hasTextAsValue ? {} : {
          callback: function (val) {
            if (typeof val === 'string') return val;
            return formatNumber(val, isPercentageSet, isCurrency);
          }
        },
        min: yAxis.min,
        max: yAxis.max && Math.round(+yAxis.max),
        border: {
          display: false,
        },
        afterUpdate: function (scaleInstance) {
          scaleInstance.width = 50; // sets the width to 100px
        },
        adapters: {
          date: {
            locale: Locales[language],
          }
        },
      }
    },
    plugins: {
      legend: {
        display: false,
        position: 'bottom' as const,
      },
      title: {
        display: false,
      },
      zoom: {
        limits: {
          x: getZoomLimits(xAxis),
          y: getZoomLimits(yAxis),
        },
        pan: {
          enabled: true,
          mode: 'xy',
          threshold: 1,
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'xy',
        }
      },
      annotation: {
        annotations: annotations.filter(({ key }) => annotationVisibility ? annotationVisibility[key] : true).reduce((acc, { key, label, xAxisLimits, yAxisLimits }, idx) => {
          acc[key] = {
            type: 'line',
            borderColor: COLORS[COLORS.length - 1 - (idx % COLORS.length)],
            borderWidth: 1,
            borderDash: [5, 2],
            z: -300,
            yMin: yAxisLimits?.min ? (typeof yAxisLimits.min === 'string' ? new Date(yAxisLimits.min).valueOf() : yAxisLimits.min) : undefined,
            yMax: yAxisLimits?.max ? (typeof yAxisLimits.max === 'string' ? new Date(yAxisLimits.max).valueOf() : yAxisLimits.max) : undefined,
            xMin: xAxisLimits?.min ? (typeof xAxisLimits.min === 'string' ? new Date(xAxisLimits.min).valueOf() : xAxisLimits.min) : undefined,
            xMax: xAxisLimits?.max ? (typeof xAxisLimits.max === 'string' ? new Date(xAxisLimits.max).valueOf() : xAxisLimits.max) : undefined,
            label: {
              content: label,
              color: BG,
              display: true,
              backgroundColor: 'transparent',
              position: 'start',
              xAdjust: -10,
              yAdjust: 6,
              z: -250,
              font: {
                weight: 'normal',
                size: 9,
              }
            },
          };
          return acc;
        }, {}),
      },
      datalabels: {
        clip: true,
        display: true,
        anchor: 'end',
        align: 'top',
        color: '#454546',
        offset: -2,
        font: { size: 9 },
        formatter: (value: any, context: any) => {
          return formatNumber(value, isPercentageSet, isCurrency);
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            if (isPercentageSet) {
              const value = context.parsed.y;
              return `${value.toFixed(2)}%`;
            }
          },
          title(context: TooltipItem<"line">[]): string | string[] | void {
            if (isTimeBased && xAxis.hasTextAsValue) {
              const parsedDate = parse(context[0].label, "MMM d, yyyy, h:mm:ss a", new Date());
              const datekey = `${format(parsedDate, "yyyy-MM-dd")}T10:00:00Z`;
              return customLabels[datekey] || context[0].label;
            }

            return context[0].label;
          }
        }
      }
    },
  };

  return options;
}

const getZoomLimits = (axis: Axis) => {
  if (!axis.max || !axis.min) return {}

  return {
    min: typeof axis.min === 'string' ? new Date(axis.min).valueOf() : axis.min,
    max: typeof axis.max === 'string' ? new Date(axis.max).valueOf() : axis.max,
  }
}

const formatNumber = (number: number, isPercentageSet = false, isCurrency = true) => {
  if(typeof number === 'string')
    return number;
  if(isPercentageSet)
    return `${number.toFixed(2)}%`;
  return formatMoneyMK(number, isCurrency ? undefined : { currencySymbol: '' });
}
